import React, { useState, useEffect, useRef } from 'react';
import { Phone, PhoneOff, Volume2, Mic, Settings, Play, Save, Loader2, Brain, MessageSquare, Sliders, User, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { toast } from 'sonner';
import { RetellWebClient } from 'retell-client-js-sdk';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoiceChat = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('test'); // 'test' or 'settings'
  const [status, setStatus] = useState('idle'); // idle, connecting, connected, speaking, listening
  const [isCallActive, setIsCallActive] = useState(false);
  const [agentSettings, setAgentSettings] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  const [playingVoice, setPlayingVoice] = useState(null);
  
  // Voice settings state
  const [voiceSettings, setVoiceSettings] = useState({
    agent_name: '',
    voice_id: '11labs-Adrian',
    max_call_duration_minutes: 30,
    voice_speed: 1.0,
    volume: 1.0,
    interruption_sensitivity: 0.9,
    language: 'en-US',
    background_noise: 'off',  // Added background noise setting
  });
  
  // Background noise options that Retell supports
  const backgroundNoiseOptions = [
    { value: 'off', label: 'No Background Sound' },
    { value: 'office', label: 'Office Environment' },
    { value: 'coffee-shop', label: 'Coffee Shop' },
    { value: 'summer-outdoor', label: 'Outdoor / Summer' },
    { value: 'mountain-outdoor', label: 'Mountain / Nature' },
    { value: 'static-noise', label: 'White Noise / Static' },
    { value: 'call-center', label: 'Call Center' },
  ];
  
  // AI config state
  const [aiConfig, setAiConfig] = useState({
    greeting: '',
    system_prompt: '',
    ai_persona_name: '',
  });
  
  const retellClientRef = useRef(null);

  useEffect(() => {
    fetchMyAgentSettings();
    fetchVoices();
    
    return () => {
      if (retellClientRef.current) {
        retellClientRef.current.stopCall();
      }
    };
  }, [token]);

  const fetchVoices = async () => {
    try {
      const res = await axios.get(`${API}/retell/voices`);
      setVoices(res.data.voices || []);
    } catch (err) {
      console.error('Error fetching voices:', err);
    }
  };

  const fetchMyAgentSettings = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/business/my-agent-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentSettings(res.data);
      
      // Populate settings forms if agent exists
      if (res.data?.has_agent && res.data?.agent_settings) {
        const settings = res.data.agent_settings;
        setVoiceSettings({
          agent_name: settings.agent_name || '',
          voice_id: settings.voice_id || '11labs-Adrian',
          max_call_duration_minutes: settings.max_call_duration_minutes || 30,
          voice_speed: settings.voice_speed || 1.0,
          volume: settings.volume || 1.0,
          interruption_sensitivity: settings.interruption_sensitivity || 0.9,
          language: settings.language || 'en-US',
        });
        setAiConfig({
          greeting: settings.greeting || res.data.greeting || '',
          system_prompt: settings.system_prompt || res.data.system_prompt || '',
          ai_persona_name: settings.ai_persona_name || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch agent settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save AI settings
  const handleSaveSettings = async () => {
    if (!token || !agentSettings?.business_id) {
      toast.error('Please log in and complete onboarding first');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/business/my-agent-settings`,
        {
          ...voiceSettings,
          ...aiConfig,
          background_noise: voiceSettings.background_noise || 'off'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('AI settings saved successfully!');
      fetchMyAgentSettings(); // Refresh
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const startCall = async () => {
    if (!agentSettings?.has_agent || !agentSettings?.agent_settings?.agent_id) {
      toast.error('No AI agent configured. Complete onboarding first.');
      return;
    }

    try {
      setStatus('connecting');
      
      // Get web call access token
      const res = await axios.post(`${API}/retell/web-call`, {
        agent_id: agentSettings.agent_settings.agent_id
      });

      // Initialize Retell client
      retellClientRef.current = new RetellWebClient();
      
      // Set up event listeners
      retellClientRef.current.on('call_started', () => {
        setStatus('connected');
        setIsCallActive(true);
        toast.success('Call connected');
      });

      retellClientRef.current.on('call_ended', () => {
        setStatus('idle');
        setIsCallActive(false);
        toast.info('Call ended');
      });

      retellClientRef.current.on('agent_start_talking', () => {
        setStatus('speaking');
      });

      retellClientRef.current.on('agent_stop_talking', () => {
        setStatus('listening');
      });

      retellClientRef.current.on('update', (update) => {
        if (update.transcript) {
          setTranscript(update.transcript);
        }
      });

      retellClientRef.current.on('error', (error) => {
        console.error('Retell error:', error);
        toast.error('Call error');
        setStatus('idle');
        setIsCallActive(false);
      });

      // Start the call
      await retellClientRef.current.startCall({
        accessToken: res.data.access_token,
      });

    } catch (err) {
      console.error('Start call error:', err);
      toast.error('Failed to start call');
      setStatus('idle');
    }
  };

  const endCall = () => {
    if (retellClientRef.current) {
      retellClientRef.current.stopCall();
    }
    setStatus('idle');
    setIsCallActive(false);
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connecting': return 'bg-yellow-500';
      case 'connected': return 'bg-green-500';
      case 'speaking': return 'bg-blue-500';
      case 'listening': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'speaking': return 'AI Speaking';
      case 'listening': return 'Listening';
      default: return 'Ready';
    }
  };

  const getOrbClass = () => {
    if (status === 'listening') return 'voice-orb listening';
    if (status === 'speaking') return 'voice-orb speaking';
    return 'voice-orb';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!agentSettings?.has_agent) {
    return (
      <div className="min-h-screen bg-background p-4 lg:p-12" data-testid="build-ai-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
              TALK TO YOUR AI
            </h1>
            <p className="text-muted-foreground text-lg">
              Test your receptionist in the browser. Change its prompt and voice under My AI.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="card-modern p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Dedicated Phone Number</h3>
              <p className="text-muted-foreground text-sm">
                Get a local phone number that your AI answers 24/7. Forward your existing number or use a new one.
              </p>
            </Card>

            <Card className="card-modern p-6">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
                <Volume2 className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Natural Voice Conversations</h3>
              <p className="text-muted-foreground text-sm">
                Ultra-realistic AI voice that sounds human. Handles complex conversations naturally.
              </p>
            </Card>

            <Card className="card-modern p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center mb-4">
                <Settings className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Fully Customizable</h3>
              <p className="text-muted-foreground text-sm">
                Train your AI on your business. Set custom greetings, FAQs, booking rules, and personality.
              </p>
            </Card>

            <Card className="card-modern p-6">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center mb-4">
                <Mic className="w-6 h-6 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Live Test Calls</h3>
              <p className="text-muted-foreground text-sm">
                Test your AI right from your browser before going live. Hear exactly how callers will experience it.
              </p>
            </Card>
          </div>

          {/* CTA */}
          <Card className="card-modern p-8 text-center bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/30">
            <h2 className="text-2xl font-bold text-foreground mb-4">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Subscribe to Front Desk Pro Pro and we'll set up your custom AI receptionist within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/pricing')} 
                size="lg"
                className="bg-primary hover:bg-primary/90"
              >
                View Pricing
              </Button>
              <Button 
                onClick={() => navigate('/try-demo')} 
                variant="outline"
                size="lg"
              >
                Try Free Demo First
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-12" data-testid="voice-chat-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">BUILD YOUR AI</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {agentSettings.business_name} • {agentSettings.agent_settings?.agent_name || 'Front Desk Pro'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('test')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'test' 
                ? 'bg-primary text-white' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Phone className="w-4 h-4 inline mr-2" />
            Live Test
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'settings' 
                ? 'bg-primary text-white' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            AI Settings
          </button>
        </div>

        {/* Live Test Tab */}
        {activeTab === 'test' && (
          <>
          <div className="grid lg:grid-cols-2 gap-6">
          {/* Voice Interface */}
          <div className="flex flex-col items-center justify-center py-8">
            {/* Voice Orb */}
            <div className={getOrbClass()}>
              <div className="w-full h-full flex items-center justify-center">
                {status === 'speaking' && (
                  <div className="waveform">
                    <div className="waveform-bar" /><div className="waveform-bar" />
                    <div className="waveform-bar" /><div className="waveform-bar" />
                    <div className="waveform-bar" />
                  </div>
                )}
                {status === 'listening' && <Mic className="w-12 h-12 text-red-500 animate-pulse" />}
                {status === 'connecting' && <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />}
                {status === 'idle' && <Volume2 className="w-12 h-12 text-gray-500" />}
                {status === 'connected' && <Mic className="w-12 h-12 text-green-500" />}
              </div>
            </div>

            {/* Status */}
            <div className="mt-6 text-center">
              <p className={`text-lg ${status === 'listening' ? 'text-red-400' : status === 'speaking' ? 'text-blue-400' : 'text-gray-400'}`}>
                {status === 'idle' && 'Click Start Call to begin'}
                {status === 'connecting' && 'Connecting...'}
                {status === 'connected' && 'Connected - speak now'}
                {status === 'listening' && '🎤 Listening...'}
                {status === 'speaking' && '🔊 AI Speaking...'}
              </p>
            </div>

            {/* Call Button */}
            <div className="mt-8">
              {!isCallActive ? (
                <Button 
                  onClick={startCall} 
                  className="btn-primary px-12 py-8 text-xl"
                  disabled={status === 'connecting'}
                  data-testid="start-call-btn"
                >
                  <Phone className="w-6 h-6 mr-3" /> START CALL
                </Button>
              ) : (
                <Button 
                  onClick={endCall} 
                  className="bg-red-600 hover:bg-red-700 text-foreground px-12 py-8 text-xl"
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-6 h-6 mr-3" /> END CALL
                </Button>
              )}
            </div>

            {/* Natural conversation note */}
            {!isCallActive && (
              <p className="mt-4 text-sm text-muted-foreground text-center">
                No phone buttons needed — just speak naturally
              </p>
            )}
          </div>

          {/* Conversation Panel */}
          <Card className="card-modern h-[500px] flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">LIVE TRANSCRIPT</h2>
              {isCallActive && (
                <span className="flex items-center gap-2 text-green-400 text-sm">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {transcript.length === 0 ? (
                <p className="text-gray-500 text-center mt-8">
                  {isCallActive ? 'Waiting for conversation...' : 'Start a call to see transcript'}
                </p>
              ) : (
                <div className="space-y-3">
                  {transcript.map((item, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded ${
                        item.role === 'user' 
                          ? 'bg-red-500/20 border-l-4 border-red-500' 
                          : 'bg-blue-500/20 border-l-4 border-blue-500'
                      }`}
                    >
                      <span className={`text-xs font-bold ${item.role === 'user' ? 'text-red-400' : 'text-blue-400'}`}>
                        {item.role === 'user' ? 'YOU' : 'AI'}
                      </span>
                      <p className="text-foreground mt-1">{item.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Info Cards */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="card-modern p-4">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <div className="w-2 h-2 bg-green-400 rounded-full" />
              <span className="text-sm font-bold">INSTANT</span>
            </div>
            <p className="text-muted-foreground text-sm">Real-time responses, no delay</p>
          </div>
          <div className="card-modern p-4">
            <div className="flex items-center gap-2 text-blue-400 mb-2">
              <Mic className="w-4 h-4" />
              <span className="text-sm font-bold">NATURAL</span>
            </div>
            <p className="text-muted-foreground text-sm">Just talk, no buttons needed</p>
          </div>
          <div className="card-modern p-4">
            <div className="flex items-center gap-2 text-purple-400 mb-2">
              <Settings className="w-4 h-4" />
              <span className="text-sm font-bold">SMART</span>
            </div>
            <p className="text-muted-foreground text-sm">Knows your business inside out</p>
          </div>
        </div>
        </>
        )}

        {/* AI Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Voice & Behavior Settings */}
            <Card className="card-modern p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Voice & Behavior</h3>
                  <p className="text-xs text-muted-foreground">Configure how your AI sounds and behaves</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* AI Name */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">AI Name</label>
                  <Input
                    value={voiceSettings.agent_name}
                    onChange={(e) => setVoiceSettings({...voiceSettings, agent_name: e.target.value})}
                    placeholder="e.g., Sarah, Alex, Reception"
                    className="bg-muted border-border"
                  />
                </div>

                {/* Voice Selection */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Voice</label>
                  <Select 
                    value={voiceSettings.voice_id} 
                    onValueChange={(val) => setVoiceSettings({...voiceSettings, voice_id: val})}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select voice" />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map(voice => (
                        <SelectItem key={voice.voice_id} value={voice.voice_id}>
                          {voice.voice_name} ({voice.gender}, {voice.accent})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Voice Speed */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">
                    Voice Speed: {voiceSettings.voice_speed?.toFixed(1)}x
                  </label>
                  <Slider
                    value={[voiceSettings.voice_speed || 1.0]}
                    onValueChange={([val]) => setVoiceSettings({...voiceSettings, voice_speed: val})}
                    min={0.5}
                    max={2.0}
                    step={0.1}
                    className="w-full"
                  />
                </div>

                {/* Max Call Duration */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Max Call Duration (minutes)</label>
                  <Input
                    type="number"
                    value={voiceSettings.max_call_duration_minutes}
                    onChange={(e) => setVoiceSettings({...voiceSettings, max_call_duration_minutes: parseInt(e.target.value)})}
                    min={1}
                    max={60}
                    className="bg-muted border-border"
                  />
                </div>

                {/* Interruption Sensitivity */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">
                    Interruption Sensitivity: {(voiceSettings.interruption_sensitivity * 100).toFixed(0)}%
                  </label>
                  <Slider
                    value={[voiceSettings.interruption_sensitivity || 0.9]}
                    onValueChange={([val]) => setVoiceSettings({...voiceSettings, interruption_sensitivity: val})}
                    min={0}
                    max={1}
                    step={0.1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Higher = AI pauses more easily when you speak
                  </p>
                </div>

                {/* Background Sound */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Background Sound</label>
                  <Select 
                    value={voiceSettings.background_noise || 'off'} 
                    onValueChange={(val) => setVoiceSettings({...voiceSettings, background_noise: val})}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select background" />
                    </SelectTrigger>
                    <SelectContent>
                      {backgroundNoiseOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Adds ambient sound during calls for a realistic experience
                  </p>
                </div>
              </div>
            </Card>

            {/* AI Personality & Script */}
            <Card className="card-modern p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">AI Personality & Script</h3>
                  <p className="text-xs text-muted-foreground">Define what your AI says and knows</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Greeting */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Greeting Message</label>
                  <Textarea
                    value={aiConfig.greeting}
                    onChange={(e) => setAiConfig({...aiConfig, greeting: e.target.value})}
                    placeholder="Thank you for calling [Business Name], how can I help you today?"
                    rows={3}
                    className="bg-muted border-border"
                  />
                </div>

                {/* System Prompt */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">AI Instructions</label>
                  <Textarea
                    value={aiConfig.system_prompt}
                    onChange={(e) => setAiConfig({...aiConfig, system_prompt: e.target.value})}
                    placeholder="You are a helpful receptionist for [Business]. You help customers book appointments, answer questions about services and pricing..."
                    rows={8}
                    className="bg-muted border-border"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Tell your AI what it should know and how to handle calls
                  </p>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <div className="lg:col-span-2 flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-primary hover:bg-primary/90 px-8"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceChat;
